import { useEffect, useRef, useState } from 'react';
import { postShowRequest } from './utils/postShowRequest';
import { MessageFromFrame, MessageScenarios } from './types';
import { executeMessageScenarios } from './utils/handleFrameMessage';
import { GPTGameNameType } from '../../types';
import { postOrientationChange } from './utils/postOrientationChange';
import { extractPromocodeFromMessage } from './utils/extractPromocodeFromMessage';
import { useModalContext } from 'src/hooks/useModalContext';

const isDebug = localStorage.getItem('gptFrameDebug') === 'true';

export const useGameFrameMessages = ({
  gameName,
  onDeposit,
  onRegistration
}: {
  gameName: GPTGameNameType;
  onDeposit?: (params: { promocode: string }) => void;
  onRegistration?: (params: { promocode: string }) => void;
}) => {
  const { closeModal } = useModalContext();
  const messagesRef = useRef<HTMLIFrameElement>(null);
  const [isGameReady, setIsGameReady] = useState(false);

  // Эффект, который хендлит сообщения, приходящие на React клиент с iframe игры
  useEffect(() => {
    const initialOrientation = window.matchMedia(`(orientation: landscape)`);

    let handleOrientationChange: ((e: MediaQueryListEvent) => void) | undefined;

    const messageScenarios: MessageScenarios = {
      LOADED: () => {
        const gameFrame = messagesRef.current;
        if (gameFrame) {
          postShowRequest(gameFrame, gameName);
          handleOrientationChange = (e: MediaQueryListEvent) => {
            postOrientationChange(gameFrame, gameName)(e.matches);
          };
          postOrientationChange(gameFrame, gameName)(initialOrientation.matches);
          initialOrientation.addEventListener('change', handleOrientationChange);
        }
      },
      MODAL_DEPOSIT: (messageFromFrame) => {
        const promocode = extractPromocodeFromMessage(messageFromFrame);
        onDeposit?.({ promocode });
      },
      SHOW_RESPONSE: () => {
        setIsGameReady(true);
      },
      QUIT: () => {
        closeModal();
      },
      QUIT_AND_HIDE: () => {
        closeModal();
      },
      QUIT_AND_REGISTER: (messageFromFrame) => {
        const promocode = extractPromocodeFromMessage(messageFromFrame);
        onRegistration?.({ promocode });
      }
    };

    /**
     * Ивент-хендлер для сообщений с юнити клиента
     */
    const handleFrameMessage = (event: MessageEvent<MessageFromFrame>) => {
      executeMessageScenarios(
        (messageFromFrame) => {
          if (isDebug) {
            console.log({ event, messageFromFrame });
          }
          const messageType = messageFromFrame.type as keyof MessageScenarios;
          messageScenarios[messageType]?.(messageFromFrame);
        },
        {
          messageEvent: event,
          gameFrame: messagesRef.current,
          openedGameName: gameName
        }
      );
    };

    window.addEventListener('message', handleFrameMessage);

    return () => {
      window.removeEventListener('message', handleFrameMessage);
      if (handleOrientationChange) {
        initialOrientation.removeEventListener('change', handleOrientationChange);
      }
    };
  }, []);

  return { messagesRef, isGameReady };
};
